import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, RotateCw, ChevronRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function StudyMode({ deckName, cards, onExit }) {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);

  // Advance ONLY when the user explicitly asks (Next button / ArrowRight). No auto-advance.
  const advance = useCallback(() => {
    setFlipped(false);
    setTimeout(() => {
      setIndex((i) => {
        if (i + 1 >= cards.length) {
          setDone(true);
          return i;
        }
        return i + 1;
      });
    }, 200); // brief delay only to let the card flip back to the front first
  }, [cards.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setFlipped((f) => !f);
      } else if (e.code === "ArrowRight") {
        advance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance]);

  const restart = () => {
    setDone(false);
    setFlipped(false);
    setIndex(0);
  };

  if (!cards.length) return null;

  if (done) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-8" data-testid="study-complete">
        <CheckCircle2 className="h-14 w-14 text-[#003366]" />
        <h2 className="font-display font-extrabold text-3xl tracking-tight text-slate-900 mt-6">Session complete</h2>
        <p className="text-slate-500 mt-2">You reviewed all {cards.length} cards in {deckName}.</p>
        <div className="flex items-center gap-3 mt-8">
          <Button variant="outline" onClick={restart} data-testid="study-restart-btn" className="rounded-full h-11 px-6 border-slate-300">
            Study again
          </Button>
          <Button onClick={onExit} data-testid="study-done-exit-btn" className="rounded-full h-11 px-6 bg-[#003366] hover:bg-[#00284d] text-white">
            Back to deck
          </Button>
        </div>
      </div>
    );
  }

  const card = cards[index];
  const isLast = index + 1 >= cards.length;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="study-mode">
      <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-100">
        <Button variant="ghost" onClick={onExit} data-testid="study-exit-btn" className="rounded-full text-slate-600 hover:text-[#003366]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Exit
        </Button>
        <div className="font-display font-bold tracking-tight text-slate-900 truncate max-w-[40vw]">{deckName}</div>
        <div className="text-sm text-slate-400 tabular-nums w-16 text-right" data-testid="study-progress">
          {index + 1} / {cards.length}
        </div>
      </div>

      <div className="h-1 w-full bg-slate-100">
        <div className="h-full bg-[#003366] transition-all duration-300" style={{ width: `${((index + 1) / cards.length) * 100}%` }} />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-6 p-6 md:p-10">
        <div className="flip-card w-full max-w-2xl h-[300px] sm:h-[340px] md:h-[380px] max-h-[46vh] shrink-0 cursor-pointer"
          onClick={() => setFlipped((f) => !f)} data-testid="flashcard">
          <div className={`flip-inner ${flipped ? "is-flipped" : ""}`}>
            <div className="flip-face rounded-3xl border border-slate-200 bg-slate-50 p-8 md:p-10 text-center shadow-sm overflow-auto">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">Front</p>
                <p className="font-display text-2xl md:text-4xl font-bold text-slate-900 leading-snug break-words">{card.front}</p>
              </div>
            </div>
            <div className="flip-face flip-back rounded-3xl border border-[#003366] bg-[#003366] p-8 md:p-10 text-center shadow-sm overflow-auto">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/50 mb-4">Back</p>
                <p className="font-display text-2xl md:text-4xl font-bold text-white leading-snug break-words">{card.back}</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-400 shrink-0">Tap the card or press Space to reveal the answer</p>

        <div className="flex items-center gap-3 shrink-0 pb-2">
          <Button variant="outline" onClick={() => setFlipped((f) => !f)} data-testid="study-flip-btn"
            className="rounded-full border-slate-300 h-11 px-6">
            <RotateCw className="h-4 w-4 mr-2" /> Flip
          </Button>
          <Button onClick={advance} data-testid="study-next-btn"
            className="rounded-full bg-[#003366] hover:bg-[#00284d] text-white h-11 px-6">
            {isLast ? "Finish" : "Next card"} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
